import { useState } from 'react';
import Modal from './Modal';
import Icon from './Icon';
import { Storage } from '../storage';

// ─── FEEDBACK MODAL ───────────────────────────────────────────────────────────
//
//  Lets logged-in OR anonymous users send feedback to the admin.  Anonymous
//  submissions get an optional Email field for follow-up; the server stores
//  the email inline at the top of the body so admin can reply.
//
//  Props:
//    open        {boolean}
//    onClose     {function}
//    currentUser {object|null}   null for anonymous submitters
//

const TYPES = [
  { value: 'bug',             label: 'Bug Report'      },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'general',         label: 'General Feedback' },
  { value: 'complaint',       label: 'Complaint'       },
  { value: 'praise',          label: 'Praise'          },
];

const SUBJECT_MAX = 200;
const BODY_MAX    = 2000;

const FeedbackModal = ({ open, onClose, currentUser }) => {
  const [type,    setType]    = useState('general');
  const [subject, setSubject] = useState('');
  const [body,    setBody]    = useState('');
  const [email,   setEmail]   = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done,    setDone]    = useState(false);
  const [error,   setError]   = useState('');

  const reset = () => {
    setType('general'); setSubject(''); setBody(''); setEmail('');
    setSubmitting(false); setDone(false); setError('');
  };
  const handleClose = () => { if (!submitting) { reset(); onClose(); } };

  const handleSubmit = async () => {
    setError('');
    if (!subject.trim()) { setError('Subject is required.'); return; }
    if (!body.trim())    { setError('Body is required.');    return; }
    setSubmitting(true);
    try {
      await Storage.submitFeedback({
        type,
        subject: subject.trim().slice(0, SUBJECT_MAX),
        body:    body.trim().slice(0, BODY_MAX),
        email:   !currentUser ? email.trim().slice(0, 200) : undefined,
      });
      setDone(true);
    } catch (err) {
      setError(err.message || 'Could not send feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="Send feedback" width={520}>
      {done ? (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <Icon name="check" size={28} />
          <p style={{ fontSize: 15, fontWeight: 600, marginTop: 10 }}>Thanks for your feedback.</p>
          <p style={{ fontSize: 13, color: 'var(--text3)', marginTop: 4 }}>We read everything.</p>
          <button
            className="btn-primary btn-sm"
            style={{ marginTop: 18 }}
            onClick={handleClose}
          >Done</button>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text2)' }}>
              Type
              <select value={type} onChange={e => setType(e.target.value)} disabled={submitting}>
                {TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text2)' }}>
              Subject
              <input
                type="text"
                value={subject}
                onChange={e => setSubject(e.target.value.slice(0, SUBJECT_MAX))}
                placeholder="A short summary"
                maxLength={SUBJECT_MAX}
                disabled={submitting}
              />
              <span style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'right' }}>{subject.length}/{SUBJECT_MAX}</span>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text2)' }}>
              Details
              <textarea
                value={body}
                onChange={e => setBody(e.target.value.slice(0, BODY_MAX))}
                placeholder="What happened, what would you like, or what's on your mind?"
                rows={6}
                maxLength={BODY_MAX}
                disabled={submitting}
                style={{ resize: 'vertical', fontFamily: 'inherit' }}
              />
              <span style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'right' }}>{body.length}/{BODY_MAX}</span>
            </label>
            {!currentUser && (
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text2)' }}>
                Email <span style={{ color: 'var(--text3)' }}>(optional — only if you'd like a reply)</span>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  maxLength={200}
                  disabled={submitting}
                />
              </label>
            )}
            {error && (
              <p role="alert" style={{ fontSize: 12, color: 'var(--danger)', margin: 0 }}>{error}</p>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
            <button className="btn-secondary btn-sm" onClick={handleClose} disabled={submitting}>Cancel</button>
            <button className="btn-primary btn-sm" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Sending…' : 'Submit'}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default FeedbackModal;
